import { useState, useEffect } from 'react';
import { X, Sparkles, Crown, Check } from 'lucide-react';
import { previewUpgrade, executeUpgrade } from '../../lib/stripe';
import { useAuth } from '../../hooks/useAuth';

const FMT_USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const FMT_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

function fmt(n) { return FMT_USD.format(n ?? 0); }
function fmtDate(iso) {
  if (!iso) return '—';
  return FMT_DATE.format(new Date(iso));
}

export default function UpgradeModal({ toTier, onClose }) {
  // For tier upgrades (pro → premium) we show an interval picker first.
  // For interval-only upgrades (monthly → annual) we skip straight to confirm.
  const { refreshEntitlement } = useAuth();

  const [interval, setInterval] = useState(null);        // null = picking
  const [previews, setPreviews] = useState({});           // { monthly: {...}, annual: {...} }
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  // Fetch both interval previews in parallel on mount
  useEffect(() => {
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);

    Promise.all([
      previewUpgrade(toTier, 'monthly').catch(e => ({ _error: e.message })),
      previewUpgrade(toTier, 'annual').catch(e => ({ _error: e.message })),
    ]).then(([mo, ann]) => {
      if (cancelled) return;
      setPreviews({ monthly: mo, annual: ann });
      setPreviewLoading(false);
    });

    return () => { cancelled = true; };
  }, [toTier]);

  async function handleConfirm() {
    setConfirming(true);
    setError(null);
    try {
      await executeUpgrade(toTier, interval);
      await refreshEntitlement();
      setDone(true);
      setTimeout(onClose, 2200);
    } catch (e) {
      setError(e.message);
      setConfirming(false);
    }
  }

  const selected = interval ? previews[interval] : null;
  const tierLabel = toTier === 'premium' ? 'Premium' : 'Pro';
  const TierIcon = toTier === 'premium' ? Crown : Sparkles;
  const tierColor = toTier === 'premium' ? 'var(--purple)' : 'var(--amber2)';
  const tierBg = toTier === 'premium' ? 'var(--purple-s)' : 'var(--amber-s)';

  return (
    <div className="reg-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="reg-modal upgrade-modal">
        <button className="reg-close" onClick={onClose}><X size={16} /></button>

        {/* ── Header ────────────────────────────────────────────────── */}
        <div className="upgrade-modal-header">
          <span className="upgrade-modal-icon" style={{ background: tierBg, color: tierColor }}>
            <TierIcon size={18} />
          </span>
          <div>
            <div className="upgrade-modal-title">Upgrade to {tierLabel}</div>
            <div className="upgrade-modal-sub">Your payment method on file will be charged</div>
          </div>
        </div>

        {/* ── Done ──────────────────────────────────────────────────── */}
        {done && (
          <div className="upgrade-done">
            <span className="upgrade-done-icon"><Check size={22} /></span>
            <p>You're now on <strong>{tierLabel}</strong>!</p>
          </div>
        )}

        {/* ── Interval picker ───────────────────────────────────────── */}
        {!done && !interval && (
          <div className="upgrade-picker">
            {(['monthly', 'annual']).map(iv => {
              const p = previews[iv];
              const loading = previewLoading || !p;
              const isAnnual = iv === 'annual';
              return (
                <button
                  key={iv}
                  className={`upgrade-plan-card${isAnnual ? ' best' : ''}`}
                  onClick={() => !loading && !p?._error && setInterval(iv)}
                  disabled={loading || !!p?._error}
                >
                  {isAnnual && <span className="upgrade-plan-badge">Best value</span>}
                  <div className="upgrade-plan-name">{isAnnual ? 'Annual' : 'Monthly'}</div>
                  {loading ? (
                    <div className="upgrade-plan-loading" />
                  ) : p?._error ? (
                    <div className="upgrade-plan-err">Unavailable</div>
                  ) : p.immediate ? (
                    <>
                      <div className="upgrade-plan-price">{fmt(p.amount_due)} <span>today</span></div>
                      {p.credit > 0 && <div className="upgrade-plan-credit">includes {fmt(p.credit)} credit</div>}
                    </>
                  ) : (
                    <>
                      <div className="upgrade-plan-price">{fmt(p.new_price)} <span>/ {isAnnual ? 'yr' : 'mo'}</span></div>
                      <div className="upgrade-plan-credit">$0 today · starts {fmtDate(p.charge_date)}</div>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Confirm step ──────────────────────────────────────────── */}
        {!done && interval && selected && !selected._error && (
          <div className="upgrade-confirm">
            <div className="upgrade-breakdown">
              {selected.immediate ? (
                <>
                  <div className="upgrade-row">
                    <span>{tierLabel} {interval === 'annual' ? 'Annual' : 'Monthly'}</span>
                    <span>{fmt((selected.amount_due ?? 0) + (selected.credit ?? 0))}</span>
                  </div>
                  {selected.credit > 0 && (
                    <div className="upgrade-row credit">
                      <span>Credit (unused days)</span>
                      <span>−{fmt(selected.credit)}</span>
                    </div>
                  )}
                  <div className="upgrade-row total">
                    <span>Charged today</span>
                    <span>{fmt(selected.amount_due)}</span>
                  </div>
                  {selected.charge_date && (
                    <div className="upgrade-row muted">
                      <span>Next renewal</span>
                      <span>{fmtDate(selected.charge_date)}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="upgrade-row muted">
                    <span>Current plan runs until</span>
                    <span>{fmtDate(selected.charge_date)}</span>
                  </div>
                  <div className="upgrade-row">
                    <span>{tierLabel} Annual</span>
                    <span>{fmt(selected.new_price)} / yr</span>
                  </div>
                  <div className="upgrade-row total">
                    <span>Charged today</span>
                    <span>$0.00</span>
                  </div>
                  <div className="upgrade-row muted">
                    <span>First annual charge</span>
                    <span>{fmtDate(selected.charge_date)}</span>
                  </div>
                </>
              )}
            </div>

            {error && <div className="upgrade-error">{error}</div>}

            <div className="upgrade-actions">
              <button className="upgrade-back" onClick={() => { setInterval(null); setError(null); }}>
                ← Back
              </button>
              <button
                className="upgrade-cta"
                style={{ background: tierColor }}
                onClick={handleConfirm}
                disabled={confirming}
              >
                {confirming
                  ? 'Processing…'
                  : selected.immediate
                    ? `Confirm & Pay ${fmt(selected.amount_due)}`
                    : 'Confirm — no charge today'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
